import { create } from 'zustand'
import { rewriteApi, RewriteProject, RewriteSource, RewriteChannel, RewriteContent, RewriteContentVersion } from '@/services/api'

interface RewriteState {
  // Data
  projects: RewriteProject[]
  currentProject: RewriteProject | null
  sources: RewriteSource[]
  channels: RewriteChannel[]
  contents: RewriteContent[]
  currentContent: (RewriteContent & { versions?: RewriteContentVersion[] }) | null

  // UI State
  isLoading: boolean
  isGenerating: boolean
  error: string | null

  // Actions
  fetchProjects: () => Promise<void>
  fetchProject: (id: string) => Promise<void>
  createProject: (title: string, description?: string) => Promise<RewriteProject | null>
  updateProject: (id: string, data: { title?: string; description?: string; status?: string }) => Promise<void>
  deleteProject: (id: string) => Promise<void>

  fetchSources: (projectId: string) => Promise<void>
  createSource: (projectId: string, content: string, title?: string) => Promise<RewriteSource | null>
  updateSource: (id: string, data: { title?: string; content?: string }) => Promise<void>
  deleteSource: (id: string) => Promise<void>

  fetchChannels: () => Promise<void>
  createChannel: (data: {
    name: string; description?: string; icon?: string;
    structure: { sections: Array<{ type: string; title: string; maxLength?: number }> };
    toneRules?: { tone: string; rules: string[] }
  }) => Promise<RewriteChannel | null>
  updateChannel: (id: string, data: Record<string, unknown>) => Promise<void>

  fetchContents: (projectId: string) => Promise<void>
  fetchContent: (id: string) => Promise<void>
  fetchContentWithVersions: (id: string) => Promise<void>
  updateContent: (id: string, data: { title?: string; sections?: Array<{ type: string; title: string; body: string }>; status?: string }) => Promise<void>
  deleteContent: (id: string) => Promise<void>
  restoreVersion: (contentId: string, versionId: string) => Promise<boolean>

  generate: (sourceId: string, channelIds: string[], projectId: string, model?: string, guidelineIds?: Record<string, string[]>, customGuidelines?: Record<string, string[]>) => Promise<boolean>
  rewriteSection: (contentId: string, sectionIndex: number, instruction?: string, guidelineIds?: string[], customGuidelines?: string[], model?: string) => Promise<string | null>

  clearError: () => void
}

export const useRewriteStore = create<RewriteState>()((set, get) => ({
  projects: [],
  currentProject: null,
  sources: [],
  channels: [],
  contents: [],
  currentContent: null,
  isLoading: false,
  isGenerating: false,
  error: null,

  clearError: () => set({ error: null }),

  fetchProjects: async () => {
    set({ isLoading: true })
    const res = await rewriteApi.projects.list()
    if (res.success && res.data) {
      set({ projects: res.data, isLoading: false })
    } else {
      set({ isLoading: false, error: res.error?.message || 'Failed to fetch projects' })
    }
  },

  fetchProject: async (id) => {
    set({ isLoading: true })
    const res = await rewriteApi.projects.get(id)
    if (res.success && res.data) {
      set({ currentProject: res.data, isLoading: false })
    } else {
      set({ isLoading: false, error: res.error?.message })
    }
  },

  createProject: async (title, description) => {
    const res = await rewriteApi.projects.create({ title, description })
    if (res.success && res.data) {
      set((s) => ({ projects: [res.data!, ...s.projects] }))
      return res.data
    }
    set({ error: res.error?.message })
    return null
  },

  updateProject: async (id, data) => {
    const res = await rewriteApi.projects.update(id, data)
    if (res.success && res.data) {
      set((s) => ({
        currentProject: s.currentProject?.id === id ? { ...s.currentProject, ...res.data! } : s.currentProject,
        projects: s.projects.map((p) => (p.id === id ? { ...p, ...res.data! } : p)),
      }))
    } else {
      set({ error: res.error?.message })
    }
  },

  deleteProject: async (id) => {
    const res = await rewriteApi.projects.delete(id)
    if (res.success) {
      set((s) => ({ projects: s.projects.filter((p) => p.id !== id) }))
    }
  },

  fetchSources: async (projectId) => {
    const res = await rewriteApi.sources.list(projectId)
    if (res.success && res.data) {
      set({ sources: res.data })
    }
  },

  createSource: async (projectId, content, title) => {
    const res = await rewriteApi.sources.create({ projectId, content, title })
    if (res.success && res.data) {
      set((s) => ({ sources: [res.data!, ...s.sources] }))
      return res.data
    }
    set({ error: res.error?.message })
    return null
  },

  updateSource: async (id, data) => {
    const res = await rewriteApi.sources.update(id, data)
    if (res.success && res.data) {
      set((s) => ({
        sources: s.sources.map((src) => (src.id === id ? { ...src, ...res.data! } : src)),
      }))
    } else {
      set({ error: res.error?.message })
    }
  },

  deleteSource: async (id) => {
    const res = await rewriteApi.sources.delete(id)
    if (res.success) {
      set((s) => ({ sources: s.sources.filter((src) => src.id !== id) }))
    }
  },

  fetchChannels: async () => {
    const res = await rewriteApi.channels.list()
    if (res.success && res.data) {
      set({ channels: res.data })
    }
  },

  createChannel: async (data) => {
    const res = await rewriteApi.channels.create(data)
    if (res.success && res.data) {
      set((s) => ({ channels: [...s.channels, res.data!] }))
      return res.data
    }
    set({ error: res.error?.message })
    return null
  },

  updateChannel: async (id, data) => {
    const res = await rewriteApi.channels.update(id, data)
    if (res.success && res.data) {
      set((s) => ({
        channels: s.channels.map((ch) => (ch.id === id ? { ...ch, ...res.data! } : ch)),
      }))
    } else {
      set({ error: res.error?.message })
    }
  },

  fetchContents: async (projectId) => {
    const res = await rewriteApi.contents.list({ projectId })
    if (res.success && res.data) {
      set({ contents: res.data })
    }
  },

  fetchContent: async (id) => {
    const res = await rewriteApi.contents.get(id)
    if (res.success && res.data) {
      set({ currentContent: res.data })
    }
  },

  fetchContentWithVersions: async (id) => {
    const res = await rewriteApi.contents.get(id)
    if (res.success && res.data) {
      set({ currentContent: res.data })
    }
  },

  updateContent: async (id, data) => {
    const res = await rewriteApi.contents.update(id, data)
    if (res.success && res.data) {
      set((s) => ({
        currentContent: s.currentContent?.id === id ? { ...s.currentContent, ...res.data! } : s.currentContent,
        contents: s.contents.map((c) => (c.id === id ? { ...c, ...res.data! } : c)),
      }))
    }
  },

  deleteContent: async (id) => {
    const res = await rewriteApi.contents.delete(id)
    if (res.success) {
      set((s) => ({
        contents: s.contents.filter((c) => c.id !== id),
        currentContent: s.currentContent?.id === id ? null : s.currentContent,
      }))
    }
  },

  restoreVersion: async (contentId, versionId) => {
    const res = await rewriteApi.contents.restore(contentId, versionId)
    if (res.success && res.data) {
      set((s) => ({
        currentContent: s.currentContent?.id === contentId ? { ...s.currentContent, ...res.data! } : s.currentContent,
        contents: s.contents.map((c) => (c.id === contentId ? { ...c, ...res.data! } : c)),
      }))
      return true
    }
    set({ error: res.error?.message })
    return false
  },

  generate: async (sourceId, channelIds, projectId, model, guidelineIds, customGuidelines) => {
    set({ isGenerating: true, error: null })
    const res = await rewriteApi.generate.create({ sourceId, channelIds, projectId, model, guidelineIds, customGuidelines })
    if (res.success) {
      await get().fetchContents(projectId)
      set({ isGenerating: false })
      return true
    }
    set({ isGenerating: false, error: res.error?.message || 'Generation failed' })
    return false
  },

  rewriteSection: async (contentId, sectionIndex, instruction, guidelineIds, customGuidelines, model) => {
    const res = await rewriteApi.generate.rewrite({ contentId, sectionIndex, instruction, guidelineIds, customGuidelines, model })
    if (res.success && res.data) {
      return res.data.rewritten
    }
    return null
  },
}))
